/**
 * 売掛払い（ツケ払い）コンポーネント
 * 後払い・売掛登録
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Event as EventIcon,
  Check as CheckIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const CreditPayment = ({
  amount,
  orderId,
  storeId,
  onSuccess,
  onError,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    dueDate: '',
    dueDays: '30', // デフォルト30日後
    note: '',
  });

  // 入力ハンドラ
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  // 支払期限計算
  const calculateDueDate = () => {
    if (formData.dueDate) return formData.dueDate;

    const days = parseInt(formData.dueDays, 10) || 30;
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  // バリデーション
  const validateForm = () => {
    if (!formData.customerName.trim()) {
      setError('お客様名を入力してください');
      return false;
    }
    return true;
  };

  // 売掛登録
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/account-receivables', {
        storeId,
        orderId,
        customerName: formData.customerName,
        customerPhone: formData.customerPhone || undefined,
        customerEmail: formData.customerEmail || undefined,
        amount,
        dueDate: calculateDueDate(),
        note: formData.note || undefined,
      });

      if (response.data.success) {
        setSuccess(true);
        setResult(response.data.accountReceivable);
        onSuccess?.(response.data);
      } else {
        throw new Error(response.data.message || '登録に失敗しました');
      }
    } catch (err) {
      console.error('売掛登録エラー:', err);
      setError(err.response?.data?.message || err.message || '登録に失敗しました');
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  // 成功画面
  if (success && result) {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          売掛を登録しました
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          後日お支払いをお待ちしております
        </Typography>

        <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2, textAlign: 'left' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">お客様名</Typography>
            <Typography variant="body2" fontWeight="bold">{result.customerName}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">金額</Typography>
            <Typography variant="body2" fontWeight="bold">¥{result.amount?.toLocaleString()}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">お支払い期限</Typography>
            <Typography variant="body2" fontWeight="bold">
              {result.dueDate ? new Date(result.dueDate).toLocaleDateString('ja-JP') : '指定なし'}
            </Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          onClick={onCancel}
          fullWidth
        >
          閉じる
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        売掛払い（ツケ払い）
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        後日お支払いいただく売掛として登録します
      </Typography>

      {/* 金額表示 */}
      <Box sx={{ textAlign: 'center', mb: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
        <Typography variant="caption" color="warning.contrastText">
          売掛金額
        </Typography>
        <Typography variant="h4" color="warning.contrastText" fontWeight="bold">
          ¥{amount?.toLocaleString()}
        </Typography>
      </Box>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 顧客情報 */}
      <TextField
        fullWidth
        required
        label="お客様名"
        name="customerName"
        value={formData.customerName}
        onChange={handleInputChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="電話番号（任意）"
        name="customerPhone"
        value={formData.customerPhone}
        onChange={handleInputChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PhoneIcon />
            </InputAdornment>
          ),
        }}
        inputProps={{ inputMode: 'tel' }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="メールアドレス（任意）"
        name="customerEmail"
        type="email"
        value={formData.customerEmail}
        onChange={handleInputChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 2 }} />

      {/* 支払期限 */}
      <Typography variant="body2" color="text.secondary" gutterBottom>
        お支払い期限
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>期限設定</InputLabel>
        <Select
          name="dueDays"
          value={formData.dueDays}
          onChange={handleInputChange}
          label="期限設定"
        >
          <MenuItem value="7">7日後</MenuItem>
          <MenuItem value="14">14日後</MenuItem>
          <MenuItem value="30">30日後（1ヶ月）</MenuItem>
          <MenuItem value="60">60日後（2ヶ月）</MenuItem>
          <MenuItem value="custom">日付を指定</MenuItem>
        </Select>
      </FormControl>

      {formData.dueDays === 'custom' && (
        <TextField
          fullWidth
          label="支払期限日"
          name="dueDate"
          type="date"
          value={formData.dueDate}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EventIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      )}

      <TextField
        fullWidth
        label="備考（任意）"
        name="note"
        multiline
        rows={2}
        value={formData.note}
        onChange={handleInputChange}
        placeholder="メモや特記事項があれば入力"
        sx={{ mb: 2 }}
      />

      <Divider sx={{ my: 2 }} />

      {/* アクションボタン */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          sx={{ flex: 1 }}
        >
          キャンセル
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={loading || !formData.customerName.trim()}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ReceiptIcon />}
          sx={{ flex: 2 }}
        >
          {loading ? '登録中...' : '売掛として登録'}
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, textAlign: 'center' }}>
        ※ 売掛は管理画面から確認・回収管理ができます
      </Typography>
    </Box>
  );
};

export default CreditPayment;
